import {
  ReactHTML,
  ReactNode,
  CSSProperties,
  createElement,
  useEffect,
  useRef,
  DOMAttributes
} from 'react';
import { observer, ObserverCallback } from './intersection';
import { useToggle } from 'hooks';

type ChildProp = (isVisible: boolean) => ReactNode;

// TODO: Fix this wrapper to render dynamic elements
export type ObserverAttributes = {
  type?: keyof ReactHTML;
  root?: HTMLElement;
  rootId?: string;
  rootMargin?: string;
  threshold: number | number[];
  src?: string;
  className?: string;
  children?: ChildProp | ReactNode;
  onChange?: ObserverCallback;
  style?: CSSProperties;
  srcSet?: string;
  sizes?: string;
  alt?: string;
} & Pick<DOMAttributes<HTMLElement>, 'onClick' | 'onMouseOver' | 'onMouseOut'>;

export default function Observer({
  type = 'div',
  threshold,
  rootMargin,
  root,
  rootId,
  onChange,
  children,
  ...props
}: ObserverAttributes) {
  const ref = useRef<HTMLElement>();
  const [visible, setVisible] = useToggle(false);

  useEffect(() => {
    const handleChange = (isVisible: boolean) => {
      setVisible(isVisible);
      onChange && onChange(isVisible);
    };
    const instance = observer(ref.current as HTMLElement, handleChange, {
      threshold,
      rootMargin,
      root
    });
    return instance.unobserve;
  }, [setVisible, onChange, ref, threshold, rootMargin, root]);

  return createElement(
    type,
    {
      ...props,
      ref
    },
    typeof children === 'function' ? (children as ChildProp)(visible) : children
  );
}
